import React from "react";
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// Styled components for better customization
const NavPaper = styled(Paper)(({ theme, isMobile }) => ({
  borderRadius: 8,
  border: isMobile ? "none" : "1px solid rgba(0, 0, 0, 0.08)",
  boxShadow: isMobile ? "none" : "0 1px 3px rgba(0, 0, 0, 0.05)",
  width: "100%",
}));

const NavHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: 0,
  fontWeight: "bold",
  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  backgroundColor: "#f5f5f5",
}));

const StyledListItemButton = styled(ListItemButton)(
  ({ theme, depth, isSelected }) => ({
    padding: theme.spacing(1, 2),
    paddingLeft: `${16 + depth * 16}px`,
    "&.Mui-selected": {
      backgroundColor: "rgba(12, 77, 150, 0.08)",
      borderLeft: "3px solid #0c4d96",
      "&:hover": {
        backgroundColor: "rgba(12, 77, 150, 0.12)",
      },
    },
    transition: "all 0.2s ease",
  })
);

const NavigationTree = ({
  selectedSection,
  onSelectSection,
  isMobile = false,
}) => {
  const { t } = useTranslation();

  // Define the tree structure using localization keys
  const navTree = [
    {
      id: "personal-info",
      label: t("privacy-policy.navigation.sections.personal-info.title"),
      icon: "fas fa-shield-alt",
      children: [
        {
          id: "personal-info-policy",
          label: t(
            "privacy-policy.navigation.sections.personal-info.children.personal-info-policy"
          ),
          icon: "fas fa-check",
        },
        {
          id: "privacy-mark",
          label: t(
            "privacy-policy.navigation.sections.personal-info.children.privacy-mark"
          ),
          icon: "fas fa-certificate",
        },
        {
          id: "info-purpose",
          label: t(
            "privacy-policy.navigation.sections.personal-info.children.info-purpose"
          ),
          icon: "fas fa-bullseye",
        },
        {
          id: "personal-data-notice",
          label: t(
            "privacy-policy.navigation.sections.personal-info.children.personal-data-notice"
          ),
          icon: "fas fa-database",
        },
      ],
    },
    {
      id: "specific-info",
      label: t("privacy-policy.navigation.sections.specific-info.title"),
      icon: "fas fa-id-card",
      children: [
        {
          id: "specific-personal-info",
          label: t(
            "privacy-policy.navigation.sections.specific-info.children.specific-personal-info"
          ),
          icon: "fas fa-check",
        },
        {
          id: "specific-handling",
          label: t(
            "privacy-policy.navigation.sections.specific-info.children.specific-handling"
          ),
          icon: "fas fa-cog",
        },
      ],
    },
  ];

  // Track expanded state for each parent node - all expanded by default
  const [expanded, setExpanded] = React.useState({
    "personal-info": true,
    "specific-info": true,
  });

  const toggleExpand = (nodeId) => {
    setExpanded({
      ...expanded,
      [nodeId]: !expanded[nodeId],
    });
  };

  // Render tree nodes recursively - simplified for fixed display
  const renderTreeNodes = (nodes, depth = 0) => {
    return nodes.map((node) => (
      <React.Fragment key={node.id}>
        {node.children ? (
          <>
            <ListItem disablePadding>
              <StyledListItemButton
                onClick={() => {
                  toggleExpand(node.id);
                  // If collapsing, don't navigate
                  if (expanded[node.id]) return;
                  // If expanding, navigate to first child
                  onSelectSection(node.children[0].id);
                }}
                depth={depth}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <i
                    className={node.icon}
                    style={{
                      color: "#0c4d96",
                      fontSize: isMobile ? "0.95rem" : "1rem",
                    }}
                  ></i>
                </ListItemIcon>
                <ListItemText
                  primary={node.label}
                  primaryTypographyProps={{
                    fontWeight: "bold",
                    variant: "body2",
                    fontSize: isMobile ? "0.9rem" : "inherit",
                  }}
                />
                <i
                  className={
                    expanded[node.id]
                      ? "fas fa-chevron-down"
                      : "fas fa-chevron-right"
                  }
                  style={{
                    fontSize: "0.75rem",
                    color: "rgba(0, 0, 0, 0.54)",
                    marginLeft: "8px",
                  }}
                ></i>
              </StyledListItemButton>
            </ListItem>

            <Collapse in={expanded[node.id]} timeout="auto" unmountOnExit>
              {node.children.map((child) => (
                <ListItem disablePadding key={child.id}>
                  <StyledListItemButton
                    onClick={() => onSelectSection(child.id)}
                    selected={selectedSection === child.id}
                    depth={depth + 1}
                    isSelected={selectedSection === child.id}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <i
                        className={child.icon}
                        style={{
                          color: "#0c4d96",
                          fontSize: isMobile ? "0.8rem" : "0.85rem",
                        }}
                      ></i>
                    </ListItemIcon>
                    <ListItemText
                      primary={child.label}
                      primaryTypographyProps={{
                        fontSize: isMobile ? "0.85rem" : "0.85rem",
                        fontWeight:
                          selectedSection === child.id ? "bold" : "normal",
                      }}
                    />
                  </StyledListItemButton>
                </ListItem>
              ))}
            </Collapse>
          </>
        ) : (
          <ListItem disablePadding>
            <StyledListItemButton
              onClick={() => onSelectSection(node.id)}
              selected={selectedSection === node.id}
              depth={depth}
              isSelected={selectedSection === node.id}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <i
                  className={node.icon}
                  style={{
                    color: "#0c4d96",
                    fontSize: isMobile ? "0.9rem" : "1rem",
                  }}
                ></i>
              </ListItemIcon>
              <ListItemText
                primary={node.label}
                primaryTypographyProps={{
                  fontWeight: selectedSection === node.id ? "bold" : "normal",
                  variant: "body2",
                  fontSize: isMobile ? "0.85rem" : "inherit",
                }}
              />
            </StyledListItemButton>
          </ListItem>
        )}
      </React.Fragment>
    ));
  };

  return (
    <NavPaper isMobile={isMobile} elevation={0}>
      {!isMobile && (
        <NavHeader variant="subtitle1">
          {t("privacy-policy.navigation.title")}
        </NavHeader>
      )}
      <List disablePadding>{renderTreeNodes(navTree)}</List>
    </NavPaper>
  );
};

export default NavigationTree;